import { FileText, LayoutGrid, type LucideIcon, Users, Zap } from 'lucide-react';
import { BarChart3 } from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const nav: NavItem[] = [
  { to: '/tools', label: 'Tools', icon: LayoutGrid },
  { to: '/revenue', label: 'Revenue', icon: BarChart3 },
  { to: '/leads', label: 'Leads', icon: Users },
  { to: '/events', label: 'Events', icon: Zap },
  { to: '/aeo', label: 'AEO pages', icon: FileText },
];

function initialsFrom(email: string | null | undefined): string {
  if (!email) return '··';
  const local = email.split('@')[0] ?? '';
  // e.g. "holden" -> "HR" for holden@holdengr.com? Better: first letter + first letter of domain
  const domain = (email.split('@')[1] ?? '').split('.')[0] ?? '';
  const a = local[0] ?? '·';
  const b = domain[0] ?? '·';
  return (a + b).toUpperCase();
}

export function AppShell() {
  const { session } = useAuth();
  const email = session?.user?.email ?? 'holden@holdengr.com';
  const initials = initialsFrom(email);

  return (
    <div className="min-h-screen grid grid-cols-[260px_1fr] bg-background text-foreground">
      <aside className="flex flex-col border-r border-border bg-background px-4 py-5">
        {/* Brand mark */}
        <div className="px-2 pb-6">
          <div className="font-heading text-[17px] font-bold tracking-tight leading-none">
            Holden
            <span className="text-gold font-normal mx-[2px]">|</span>
            GR
          </div>
          <div className="mt-1 text-[10px] font-medium uppercase tracking-[0.18em] text-foreground/55">
            Tool Factory
          </div>
        </div>

        {/* Nav */}
        <nav className="flex flex-col gap-[2px]">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  [
                    '-ml-[2px] flex items-center gap-[10px] rounded-md px-[10px] py-2',
                    'border-l-2 text-[13px] font-medium transition-colors duration-150',
                    isActive
                      ? 'border-gold bg-secondary/60 text-foreground font-semibold'
                      : 'border-transparent text-foreground/70 hover:bg-secondary/60',
                  ].join(' ')
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      size={14}
                      strokeWidth={2}
                      className={isActive ? 'text-gold opacity-100' : 'opacity-65'}
                    />
                    <span>{n.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User chip */}
        <div className="mt-auto border-t border-border pt-4">
          <div className="flex items-center gap-[10px] px-[10px] py-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground font-heading text-[12px] font-semibold text-background">
              {initials}
            </div>
            <div className="leading-tight">
              <div className="text-[12px] font-medium text-foreground">{email}</div>
              <button
                type="button"
                onClick={() => supabase.auth.signOut()}
                className="text-[11px] text-foreground/55 hover:text-gold transition-colors cursor-pointer"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </aside>

      <main className="px-10 py-8 max-w-[1280px] w-full">
        <Outlet />
      </main>
    </div>
  );
}
